import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, ScrollView, FlatList, ActivityIndicator, Share, Alert, Keyboard, TouchableWithoutFeedback, Animated as RNAnimated } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Path } from 'react-native-svg';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, { useSharedValue, useAnimatedStyle, withRepeat, withTiming, withSequence, FadeIn, FadeOut, cancelAnimation } from 'react-native-reanimated';
import { format, isToday, isYesterday } from 'date-fns';

import BillCharacterSVG from '../../components/BillCharacterSVG';
import MessageDateBadge from '../../components/MessageDateBadge';
import MessageBubble, { MessageBubbleAnchor } from '@/components/chat/MessageBubble';
import MessageContextMenuOverlay from '@/components/chat/MessageContextMenuOverlay';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { useNotifications } from '@/contexts/NotificationContext';
import { useNotes } from '@/contexts/NotesContext';
import { useAuth } from '../../contexts/AuthContext';
import { SobrietyClockCard } from '@/components/SobrietyClockCard';
import { ChatMeetingsCarousel } from '@/components/ChatMeetingsCarousel';
import { useSobriety } from '@/contexts/SobrietyContext';
import conversationService from '@/services/conversationService';
import { Meeting } from '@/types/meetings';
import { MeetingsService } from '@/services/meetingsService';
import { useBill } from '../../contexts/BillContext';
import BillAIService from '../../services/anthropicService';
import { GalleryConsentService } from '../../services/galleryConsentService';
import LinkifiedText from '@/components/LinkifiedText';
import BillBottomNav from '@/components/BillBottomNav';
import Avatar from '@/components/Avatar';
import AvatarBlurRing from '@/components/AvatarBlurRing';
import BaseModal from '@/components/BaseModal';
import { useLanguage } from '@/contexts/LanguageContext';
import RevenueCatService from '@/services/revenueCatService';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import { BillPlusBar } from '@/components/BillPlusBar';
import { OnboardingCarousel } from '@/components/OnboardingCarousel';
import onboardingService from '@/services/onboardingService';

// Animated loading dots component
const LoadingDots = () => {
  const opacity1 = useSharedValue(0.3);
  const opacity2 = useSharedValue(0.3);
  const opacity3 = useSharedValue(0.3);

  useEffect(() => {
    const animationDuration = 600;
    const delayBetweenDots = 200;

    opacity1.value = withRepeat(
      withSequence(
        withTiming(1, { duration: animationDuration }),
        withTiming(0.3, { duration: animationDuration })
      ),
      -1,
      false
    );

    opacity2.value = withRepeat(
      withSequence(
        withTiming(0.3, { duration: delayBetweenDots }),
        withTiming(1, { duration: animationDuration }),
        withTiming(0.3, { duration: animationDuration + delayBetweenDots })
      ),
      -1,
      false
    );

    opacity3.value = withRepeat(
      withSequence(
        withTiming(0.3, { duration: delayBetweenDots * 2 }),
        withTiming(1, { duration: animationDuration }),
        withTiming(0.3, { duration: animationDuration })
      ),
      -1,
      false
    );
  }, []);

  const animatedStyle1 = useAnimatedStyle(() => ({
    opacity: opacity1.value,
  }));

  const animatedStyle2 = useAnimatedStyle(() => ({
    opacity: opacity2.value,
  }));

  const animatedStyle3 = useAnimatedStyle(() => ({
    opacity: opacity3.value,
  }));

  return (
    <View style={styles.loadingDotsContainer}>
      <Animated.View style={[styles.loadingDot, animatedStyle1]} />
      <Animated.View style={[styles.loadingDot, animatedStyle2]} />
      <Animated.View style={[styles.loadingDot, animatedStyle3]} />
    </View>
  );
};

const formatDate = (date, t) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (messageDate.getTime() === today.getTime()) {
    return t('chat.today');
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return t('chat.yesterday');
  } else {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short'
    });
  }
};

function BillScreen() {
  const [inputText, setInputText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { t } = useLanguage();

  const router = useRouter();
  const askParams = useLocalSearchParams<{ askGoal?: string; askStage?: string }>();
  const askedRef = useRef<string | null>(null);
  const { unreadCount, markAllAsRead } = useNotifications();
  const { isBillMessageSaved, saveBillMessage, unsaveBillMessage } = useNotes();
  const flatListRef = useRef<FlatList>(null);
  const scrollY = useSharedValue(0);
  const [currentDate, setCurrentDate] = useState(null);
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    messageId: string | null;
    text: string;
    isUser: boolean;
    anchor: MessageBubbleAnchor | null;
  }>({
    visible: false,
    messageId: null,
    text: '',
    isUser: false,
    anchor: null,
  });
  const [bookmarkedMessages, setBookmarkedMessages] = useState(new Set());

  // Animated date badge opacity
  const dateBadgeOpacity = useSharedValue(0);

  const animatedDateBadgeStyle = useAnimatedStyle(() => {
    return {
      opacity: dateBadgeOpacity.value,
    };
  });

  // Canonical device-local sobriety day count — single source of truth.
  // Only forward it to Bill once the record is actually loaded/set up; while
  // hydrating, getCurrentDays() returns 0, so we send undefined and let the
  // server compute its own calendar-day fallback instead of stating "0 days".
  const { getCurrentDays, sobrietyState } = useSobriety();
  const canonicalSobrietyDays = useCallback((): number | undefined => (
    sobrietyState.isSetup && !sobrietyState.loading ? getCurrentDays() : undefined
  ), [sobrietyState.isSetup, sobrietyState.loading, getCurrentDays]);

  // Messages are now managed by BillContext
  const { 
    messages: billMessages, 
    sendMessage: sendMessageToBill,
    sendDirectMessage: sendDirectMessageToBill,
    isLoading: billLoading, 
    error: billError,
    dailyLimitReached,
    remainingMessages,
    resetDailyLimit,
    updateTypingActivity,
    isInitialized,
    clearConversation,
    loadConversationHistory,
  } = useBill();
  const { user, userProfile, session } = useAuth();
  const { hasPadrinhoPlus } = useServerVerifiedPlus();

  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [achievedMilestone, setAchievedMilestone] = useState(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisibleDebounced, setIsKeyboardVisibleDebounced] = useState(false);
  const keyboardTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Navbar slide animation
  const navbarMeasuredHeight = useRef(0);
  const navbarContainerHeight = useRef(new RNAnimated.Value(200)).current;
  const navbarTranslateY = useRef(new RNAnimated.Value(0)).current;
  const onNavbarLayout = useCallback((e: any) => {
    const h = e.nativeEvent.layout.height;
    if (h > 0 && navbarMeasuredHeight.current === 0) {
      navbarMeasuredHeight.current = h;
      navbarContainerHeight.setValue(h);
    }
  }, []);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showClearConversationModal, setShowClearConversationModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // ── Proactive welcome message state ─────────────────────────────────────
  const [proactiveMessages, setProactiveMessages] = useState<Array<{
    id: string; text: string; isUser: false; timestamp: Date;
  }>>([]);
  const [isProactiveTyping, setIsProactiveTyping] = useState(false);
  const welcomeTriggeredRef = useRef(false);
  const proactiveTimersRef = useRef<NodeJS.Timeout[]>([]);

  // Initialize bookmarked messages from Notes context
  useEffect(() => {
    const syncBookmarks = () => {
      const newBookmarks = new Set();
      billMessages.forEach(message => {
        if (message.role !== 'user' && isBillMessageSaved(message.id)) {
          newBookmarks.add(message.id);
        }
      });
      setBookmarkedMessages(newBookmarks);
    };

    if (billMessages.length > 0) {
      syncBookmarks();
    }
  }, [billMessages, isBillMessageSaved]);

  // Daily limit is now handled by BillContext's subscription:status-changed event listener
  // This preserves AsyncStorage state for potential downgrade restoration

  // Helper function to determine if we should show welcome message (moved outside useMemo)
  const shouldShowWelcomeMessage = React.useMemo(() => {
    // Only show welcome if we're initialized and have no messages
    const result = isInitialized && billMessages.length === 0;
    console.log('📝 Welcome message logic:', { 
      isInitialized, 
      messageCount: billMessages.length, 
      shouldShow: result,
      messagesPreview: billMessages.slice(0, 2).map(m => ({ 
        id: m?.id, 
        role: m?.role, 
        preview: m?.content?.substring(0, 50) 
      }))
    });
    return result;
  }, [isInitialized, billMessages.length]);

  // Convert Bill messages to UI format
  const messages = React.useMemo(() => {
    console.log('🔄 Processing billMessages for UI:', { 
      count: billMessages?.length || 0, 
      isInitialized,
      billLoading,
      messages: billMessages?.slice(-3).map(m => ({ 
        id: m?.id, 
        role: m?.role, 
        contentPreview: m?.content?.substring(0, 30) + '...'
      })) || []
    });

    if (!billMessages || billMessages.length === 0) {
      console.log('📝 No billMessages to convert');
      return [];
    }

    const converted = billMessages.map((message, index) => {
      if (!message?.id || !message?.content) {
        console.warn('⚠️ Invalid message structure:', { 
          message, 
          index, 
          hasId: !!message?.id, 
          hasContent: !!message?.content,
          contentType: typeof message?.content
        });
        return null;
      }

      const convertedMessage = {
        id: message.id,
        text: message.content,
        isUser: message.role === 'user',
        timestamp: message.timestamp || new Date(),
        context: message.context,
        textPreview: message.content.substring(0, 50)
      };

      console.log('✅ Converting message:', {
        original: {
          id: message.id,
          role: message.role,
          contentLength: message.content?.length || 0,
          contentPreview: message.content?.substring(0, 30) + '...'
        },
        converted: {
          id: convertedMessage.id,
          isUser: convertedMessage.isUser,
          textLength: convertedMessage.text?.length || 0,
          textPreview: convertedMessage.textPreview
        }
      });

      return convertedMessage;
    }).filter(Boolean);

    console.log('✅ Converted messages for UI:', {
      convertedCount: converted.length,
      originalCount: billMessages.length,
      lastMessages: converted.slice(-3).map(m => ({
        id: m?.id,
        isUser: m?.isUser,
        textPreview: m?.textPreview,
        textLength: m?.text?.length || 0
      })),
      validCount: converted.filter(m => m?.id && m?.text).length,
      billLoadingState: billLoading,
      allMessagesValid: converted.every(m => m?.id && m?.text)
    });

    return converted;
  }, [billMessages, isInitialized, billLoading]);

  // Debug effect to monitor billMessages changes
  useEffect(() => {
    console.log('🔍 BillMessages changed:', { 
      count: billMessages?.length || 0,
      isLoading: billLoading,
      messages: (billMessages || []).slice(-3).map(m => ({ 
        id: m?.id, 
        role: m?.role, 
        contentPreview: (m?.content?.substring(0, 30) || '') + '...' 
      }))
    });
  }, [billMessages, billLoading]);

  // Suggestions section is disabled — keep hidden regardless of message count
  React.useEffect(() => {
    setShowSuggestions(false);
  }, [billMessages?.length, isInitialized]);


  // Dynamic keyboard height listener with auto-scroll, debounced visibility, and navbar animation
  useEffect(() => {
    // Navbar slide animation: iOS fires keyboardWillShow/Hide before animation starts
    // so we can sync perfectly; Android uses Did variants
    const navbarShowListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      (event) => {
        const dur = (event as any).duration ?? 250;
        const targetH = navbarMeasuredHeight.current;
        RNAnimated.parallel([
          RNAnimated.timing(navbarContainerHeight, {
            toValue: 0,
            duration: dur,
            useNativeDriver: false,
          }),
          RNAnimated.timing(navbarTranslateY, {
            toValue: targetH,
            duration: dur,
            useNativeDriver: true,
          }),
        ]).start();
      }
    );

    const navbarHideListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      (event) => {
        const dur = (event as any).duration ?? 250;
        const targetH = navbarMeasuredHeight.current;
        RNAnimated.parallel([
          RNAnimated.timing(navbarContainerHeight, {
            toValue: targetH,
            duration: dur,
            useNativeDriver: false,
          }),
          RNAnimated.timing(navbarTranslateY, {
            toValue: 0,
            duration: dur,
            useNativeDriver: true,
          }),
        ]).start();
      }
    );

    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', (event) => {
      // Clear any pending hide timeout
      if (keyboardTimeoutRef.current) {
        clearTimeout(keyboardTimeoutRef.current);
        keyboardTimeoutRef.current = null;
      }
      setKeyboardHeight(event.endCoordinates.height);
      setIsKeyboardVisibleDebounced(true);
      // Scroll to bottom when keyboard opens (inverted FlatList: offset 0 = bottom)
      setTimeout(() => {
        flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
      }, 100);
    });
    
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
      // Debounce hiding to prevent flickering during keyboard transitions
      keyboardTimeoutRef.current = setTimeout(() => {
        setIsKeyboardVisibleDebounced(false);
      }, 100);
    });

    return () => {
      navbarShowListener?.remove();
      navbarHideListener?.remove();
      keyboardDidShowListener?.remove();
      keyboardDidHideListener?.remove();
      if (keyboardTimeoutRef.current) {
        clearTimeout(keyboardTimeoutRef.current);
      }
    };
  }, []);

  // Auto-scroll to bottom when messages change (inverted FlatList: offset 0 = bottom)
  useEffect(() => {
    if (flatListRef.current) {
      setTimeout(() => {
        flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
      }, 100);
    }
  }, [messages]);

  // Animated breathing effect for Bill character
  const breathingScale = useSharedValue(1);

  // P09 Phase 3 (PV2-19, CL-A2): gate on focus so the Reanimated worklet
  // does not run on the native thread while another tab is visible.
  useFocusEffect(
    useCallback(() => {
      breathingScale.value = withRepeat(
        withSequence(
          withTiming(1.05, { duration: 2000 }),
          withTiming(1, { duration: 2000 })
        ),
        -1,
        false
      );
      return () => {
        cancelAnimation(breathingScale);
      };
    }, [])
  );

  const animatedBillStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: breathingScale.value }],
    };
  });

  const scrollToBottom = () => {
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
    setShowScrollToBottom(false);
  };

  // ── Proactive welcome sequence ───────────────────────────────────────────
  const interruptProactiveSequence = useCallback(() => {
    proactiveTimersRef.current.forEach(clearTimeout);
    proactiveTimersRef.current = [];
    setIsProactiveTyping(false);
  }, []);

  // Proactive greetings are now decided server-side (Proactive Message System,
  // SYS_PROACTIVE_MESSAGES.md). The client asks once per app-open and only
  // animates whatever the server returns — all eligibility + de-dup logic lives
  // on the server, so greetings can no longer repeat on cold start.
  useFocusEffect(
    useCallback(() => {
      if (!isInitialized || welcomeTriggeredRef.current) return;
      welcomeTriggeredRef.current = true;

      const run = async () => {
        if (!session?.access_token || !user?.id) return;

        let result: any = null;
        try {
          const resp = await fetch('https://mobile.padrinho.app/api/bill/proactive?trigger=app_open', {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (!resp.ok) return;
          result = await resp.json();
        } catch {
          return;
        }

        const message = result?.success ? result.message : null;
        const bubbles: string[] = message?.bubbles || [];
        if (!message || bubbles.length === 0) return;

        import('@/utils/clarityEvents')
          .then(({ logBillWelcomeShown }) =>
            logBillWelcomeShown?.({ variant: message.type === 'welcome_new' ? 'new' : 'returning' }))
          .catch(() => {});

        // Animate bubbles with a typing indicator before each.
        const now = new Date();
        let accumulated = 0;
        bubbles.forEach((text, i) => {
          const typingDelay = i === 0 ? 800 : 2000; // ms before this bubble's typing starts
          accumulated += typingDelay;
          const typingTimer = setTimeout(() => {
            setIsProactiveTyping(true);
          }, accumulated - typingDelay + (i === 0 ? 400 : 0));

          accumulated += 1000; // bubble appears after typing delay
          const bubbleTimer = setTimeout(() => {
            setIsProactiveTyping(false);
            setProactiveMessages(prev => [
              ...prev,
              { id: `proactive_${i}`, text, isUser: false as const, timestamp: new Date(now.getTime() + i * 2000) },
            ]);
          }, accumulated);

          proactiveTimersRef.current.push(typingTimer, bubbleTimer);
        });

        // After all bubbles are visible, reload conversationHistory from the
        // server. By this point the proactives are persisted in bill_conversations
        // with their correct server-side timestamps (e.g. 18:14:12), so the
        // BillContext combinedMessages will order them BEFORE any user messages
        // that arrived later. Once history confirms they are covered, we clear
        // the local proactiveMessages state (avoiding duplicates) and they
        // survive navigation because conversationHistory lives in BillContext.
        const historyTimer = setTimeout(async () => {
          const ok = await loadConversationHistory();
          if (ok) setProactiveMessages([]);
        }, accumulated + 1500); // 1.5 s buffer after the last bubble
        proactiveTimersRef.current.push(historyTimer);
      };

      run();
    }, [isInitialized, session?.access_token, user?.id, loadConversationHistory])
  );

  // Goal/Stage labels (mirrors GOAL_TRANSLATION_KEY in MissoesGoalCard).
  const ASK_GOAL_LABEL_KEY: Record<string, string> = {
    reduce_control: 'onboarding.goal.options.reduceControl',
    full_abstinence: 'onboarding.goal.options.fullAbstinence',
    maintain_sobriety: 'onboarding.goal.options.maintainSobriety',
    identify_triggers: 'onboarding.goal.options.identifyTriggers',
    healthy_habits: 'onboarding.goal.options.healthyHabits',
    rebuild_relationships: 'onboarding.goal.options.rebuildRelationships',
    monitor_progress: 'onboarding.goal.options.monitorProgress',
  };

  // Auto-send a question when arriving with ?askGoal=<key> or ?askStage=<n>.
  // Fires once per (askGoal|askStage) param value, guarded by askedRef.
  useEffect(() => {
    if (!isInitialized) return;
    if (!hasPadrinhoPlus && remainingMessages <= 0) return;

    const askGoal = typeof askParams.askGoal === 'string' ? askParams.askGoal : null;
    const askStage = typeof askParams.askStage === 'string' ? askParams.askStage : null;
    const askKey = askGoal ? `goal:${askGoal}` : askStage ? `stage:${askStage}` : null;
    if (!askKey || askedRef.current === askKey) return;

    let questionText = '';
    if (askGoal && ASK_GOAL_LABEL_KEY[askGoal]) {
      const goalLabel = t(ASK_GOAL_LABEL_KEY[askGoal]) as string;
      questionText = (t('billAsk.goalQuestion', { goal: goalLabel }) as string) || '';
    } else if (askStage) {
      questionText = (t('billAsk.stageQuestion') as string) || '';
    }
    if (!questionText) return;

    askedRef.current = askKey;
    setShowSuggestions(false);
    setUserHasInteracted(true);
    setInputText('');

    sendMessageToBill(questionText, undefined, canonicalSobrietyDays()).catch((err) => {
      console.error('❌ Bill auto-ask failed:', err);
      askedRef.current = null;
    });

    // Clear the ask params so a tab re-focus doesn't retrigger.
    try {
      router.setParams({ askGoal: undefined, askStage: undefined } as any);
    } catch {
      // best-effort — ref guard is the source of truth
    }
  }, [
    isInitialized,
    askParams.askGoal,
    askParams.askStage,
    hasPadrinhoPlus,
    remainingMessages,
    sendMessageToBill,
    canonicalSobrietyDays,
    router,
    t,
  ]);

  const sendMessage = async (text?: string) => {
    const messageText = text || inputText.trim();
    if (!messageText) return;
    
    // Plus users have unlimited messages, Base users check remaining count
    // Use server-provided remainingMessages as source of truth
    if (!hasPadrinhoPlus && remainingMessages <= 0) {
      console.log('🚫 Cannot send message: no remaining messages for Base user');
      return;
    }

    setInputText('');
    setShowSuggestions(false);
    setUserHasInteracted(true);
    interruptProactiveSequence();

    try {
      await sendMessageToBill(messageText, undefined, canonicalSobrietyDays());
    } catch (error) {
      console.error('Error sending message:', error);
      // Error handling is now managed by BillContext
    }
  };

  const handleSuggestedAction = async (actionKey) => {
      setShowSuggestions(false);
      setInputText('');
      setUserHasInteracted(true);

      // PRD §5.3 — `bill_quick_action_tapped`. action_id mirrors the
      // existing string key (`startTrail` | `diaryGuide` today; future keys
      // automatically flow through without code changes).
      import('@/utils/clarityEvents')
        .then(({ logBillQuickActionTapped }) =>
          logBillQuickActionTapped({ action_id: String(actionKey) })
        )
        .catch(() => {});

      try {
        // Determine specific guidance based on the action
        let specificContext = null;
        let action = actionKey;
        
        if (actionKey === 'startTrail') {
          specificContext = '12steps';
          action = t('chat.suggestedActions.startTrail');
        } else if (actionKey === 'diaryGuide') {
          specificContext = 'diary_guide';
          action = t('chat.suggestedActions.diaryGuide');
        }

        // Use direct message method with specific context
        await sendDirectMessageToBill(action, specificContext, canonicalSobrietyDays());
      } catch (error) {
        console.error('Error sending suggested action:', error);
      }
    };

  // Handle chat reset - show confirmation modal
  const handleChatReset = () => {
    setShowClearConversationModal(true);
  };

  const handleUpgradeToPlus = async () => {
    try {
      await RevenueCatService.presentPaywall({ fromSurface: 'home' });
    } catch (error) {
      console.error('Error showing paywall from Plus bar:', error);
    }
  };

  // Confirm and execute chat reset
  const handleClearConversationConfirm = async () => {
    try {
      setIsClearing(true);
      console.log('🔄 Chat reset confirmed by user');
      await clearConversation();
      console.log('✅ Chat reset completed');
      setShowClearConversationModal(false);
    } catch (error) {
      console.error('❌ Error resetting chat:', error);
    } finally {
      setIsClearing(false);
    }
  };

  const handleNotificationPress = () => {
    router.push('/notifications');
    markAllAsRead();
  };

  const handleScroll = (event) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    const contentHeight = event.nativeEvent.contentSize.height;
    const layoutHeight = event.nativeEvent.layoutMeasurement.height;

    // Close context menu when scrolling
    if (contextMenu.visible) {
      setContextMenu({ visible: false, messageId: null, text: '', isUser: false, anchor: null });
    }

    scrollY.value = offsetY;

    // Inverted FlatList: offsetY=0 means at the bottom (newest messages).
    // Show scroll-to-bottom button when the user has scrolled away from the newest end.
    const isNearBottom = offsetY < 100;
    setShowScrollToBottom(!isNearBottom && messages && messages.length > 0);

    // Show date badge when user has scrolled up into older messages.
    // In the reversed data array (newest at index 0), higher offsetY → older messages.
    if (offsetY > 50 && reversedMessages.length > 0) {
      const totalScrollable = Math.max(1, contentHeight - layoutHeight);
      const scrollProgress = Math.min(1, offsetY / totalScrollable);

      // Map scroll progress to reversed-array index (0=newest, n-1=oldest)
      const reversedIndex = Math.min(
        Math.floor(scrollProgress * reversedMessages.length),
        reversedMessages.length - 1
      );
      const visibleMessage = reversedMessages[reversedIndex];
      if (visibleMessage && visibleMessage.timestamp) {
        const messageDate = formatDate(visibleMessage.timestamp, t);
        if (messageDate !== currentDate) {
          setCurrentDate(messageDate);
        }
        dateBadgeOpacity.value = withTiming(1, { duration: 300 });
      }
    } else {
      dateBadgeOpacity.value = withTiming(0, { duration: 300 });
      setCurrentDate(null);
    }
  };

  const handleLongPress = useCallback((messageId: string, anchor: MessageBubbleAnchor) => {
    setContextMenu({
      visible: true,
      messageId,
      text: anchor.text,
      isUser: anchor.isUser,
      anchor,
    });
  }, []);

  const handleBookmarkToggle = useCallback((messageId: string, messageText: string) => {
    const isSaved = isBillMessageSaved(messageId);
    if (isSaved) {
      unsaveBillMessage(messageId);
      setBookmarkedMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    } else {
      saveBillMessage(messageId, messageText);
      setBookmarkedMessages(prev => {
        const newSet = new Set(prev);
        newSet.add(messageId);
        return newSet;
      });
    }
  }, [isBillMessageSaved, unsaveBillMessage, saveBillMessage]);

  const handleShareMessage = useCallback(async (messageId: string, messageText: string, isUser: boolean) => {
    const shareText = isUser
      ? messageText
      : `${messageText}\n\nMensagem do Bill no aplicativo Padrinho. Baixe em www.padrinho.app`;
    try {
      await Share.share({ message: shareText, title: t('chat.messageTitle') });
    } catch (error) {
      Alert.alert(t('common.error'), t('chat.error'));
    }
    setContextMenu({ visible: false, messageId: null, text: '', isUser: false, anchor: null });
  }, [t]);

  const handleBookmarkMessage = useCallback((messageId: string, messageText: string) => {
    handleBookmarkToggle(messageId, messageText);
    setContextMenu({ visible: false, messageId: null, text: '', isUser: false, anchor: null });
  }, [handleBookmarkToggle]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ visible: false, messageId: null, text: '', isUser: false, anchor: null });
  }, []);

  // P04: reversed so newest is at index 0, required for inverted FlatList.
  const reversedMessages = React.useMemo(
    () => [...messages, ...proactiveMessages].reverse(),
    [messages, proactiveMessages]
  );

  const renderMessage = useCallback(({ item, index }: { item: typeof messages[0]; index: number }) => {
    // In the reversed array (newest at index 0):
    //   previousMessage = older neighbour = reversedMessages[index + 1]
    //   nextMessage     = newer neighbour = reversedMessages[index - 1]
    const previousMessage = index < reversedMessages.length - 1 ? reversedMessages[index + 1] : null;
    const nextMessage = index > 0 ? reversedMessages[index - 1] : null;
    return (
      <MessageBubble
        message={item}
        previousMessage={previousMessage}
        nextMessage={nextMessage}
        isBookmarked={bookmarkedMessages.has(item.id) || (!item.isUser && isBillMessageSaved(item.id))}
        contextMenuVisible={contextMenu.visible && contextMenu.messageId === item.id}
        onLongPress={handleLongPress}
      />
    );
  }, [reversedMessages, bookmarkedMessages, isBillMessageSaved, contextMenu.visible, contextMenu.messageId, handleLongPress]);

  const handleTextChange = (text) => {
      setInputText(text);
      // Only trigger typing activity when user is actively typing
      // Don't trigger for empty text or when clearing input
      if (text.trim().length > inputText.trim().length) {
        updateTypingActivity();
      }
    };

// Show loading state if user is not available
  if (!user) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.chatContainer, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#151515" />
          <Text style={[styles.billSubtitle, { marginTop: 16 }]}>
            {t('chat.connecting')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
        keyboardVerticalOffset={0}
      >
        <View style={styles.contentWrapper}>
          <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
          <View style={styles.fullScreenWrapper}>
            <View style={styles.chatHeader}>
          <TouchableOpacity 
            style={styles.avatarButton}
            onPress={() => {
              import('@/utils/clarityEvents')
                .then(({ logUiAction }) =>
                  logUiAction({ surface: 'home', element: 'header_profile' })
                )
                .catch(() => {});
              router.push('/perfil');
            }}
          >
            {hasPadrinhoPlus ? (
              <AvatarBlurRing>
                <Avatar
                  uri={userProfile?.avatar_url}
                  size={32}
                  showInitials={true}
                  initials={userProfile?.full_name || undefined}
                  borderWidth={0}
                />
              </AvatarBlurRing>
            ) : (
              <Avatar
                uri={userProfile?.avatar_url}
                size={32}
                showInitials={true}
                initials={userProfile?.full_name || undefined}
                borderColor="#7E7D75"
                borderWidth={1}
              />
            )}
          </TouchableOpacity>
          <Text style={styles.chatHeaderTitle}>Bill</Text>
          <TouchableOpacity
            style={styles.notificationButton}
            onPress={() => {
              import('@/utils/clarityEvents')
                .then(({ logUiAction }) =>
                  logUiAction({ surface: 'home', element: 'header_notifications' })
                )
                .catch(() => {});
              handleNotificationPress();
            }}
          >
            <View style={styles.notificationIconContainer}>
              <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
                <Path 
                  fillRule="evenodd" 
                  clipRule="evenodd" 
                  d="M12.5 17.8476C18.1392 17.8476 20.7481 17.1242 21 14.2205C21 11.3188 19.1812 11.5054 19.1812 7.94511C19.1812 5.16414 16.5452 2 12.5 2C8.45477 2 5.81885 5.16414 5.81885 7.94511C5.81885 11.5054 4 11.3188 4 14.2205C4.25295 17.1352 6.86177 17.8476 12.5 17.8476Z" 
                  stroke="#151515" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <Path 
                  d="M14.8889 20.8572C13.5247 22.372 11.3967 22.3899 10.0195 20.8572" 
                  stroke="#151515" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </Svg>
              {unreadCount > 0 && (
                <View style={styles.notificationBadge}>
                  <Text style={styles.notificationBadgeText}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        </View>

        <View style={styles.chatContainerFullHeight}>
          {currentDate && (
            <Animated.View style={[styles.dateBadgeContainer, animatedDateBadgeStyle]}>
              <MessageDateBadge text={currentDate} />
            </Animated.View>
          )}
          <FlatList
            ref={flatListRef}
            data={reversedMessages}
            renderItem={renderMessage}
            keyExtractor={(item) => item.id}
            inverted
            removeClippedSubviews
            maxToRenderPerBatch={10}
            windowSize={5}
            style={styles.scrollViewFullHeight}
            contentContainerStyle={[styles.chatContentWithPadding, { flexGrow: 1, paddingVertical: 16 }]}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            ListHeaderComponent={
              (billLoading && userHasInteracted) || isProactiveTyping ? (
                <Animated.View
                  entering={FadeIn.duration(300)}
                  exiting={FadeOut.duration(200)}
                  style={[styles.messageContainer, styles.agentMessage, { marginBottom: 16 }]}
                >
                  <View style={styles.loadingContainer}>
                    <LoadingDots />
                    <Text style={[styles.messageText, styles.agentMessageText, styles.loadingText]}>
                      {t('bill.loading.typing')}
                    </Text>
                  </View>
                </Animated.View>
              ) : null
            }
            ListFooterComponent={
              <View style={styles.welcomeLayout}>
                <BillCharacterSVG width={280} height={207} />
                <Text style={styles.billTitle}>{t('bill.welcome.title')}</Text>
                <Text style={styles.billSubtitle}>{t('bill.welcome.subtitle')}</Text>
              </View>
            }
          />
        </View>



        {/* Scroll to bottom button - positioned above input area */}
        {showScrollToBottom && (
          <Animated.View 
            entering={FadeIn.duration(200)} 
            exiting={FadeOut.duration(200)}
            style={styles.scrollToBottomContainer}
          >
            <TouchableOpacity 
              style={styles.scrollToBottomButton}
              onPress={() => {
                import('@/utils/clarityEvents')
                  .then(({ logUiAction }) =>
                    logUiAction({ surface: 'home', element: 'scroll_to_bottom' })
                  )
                  .catch(() => {});
                scrollToBottom();
              }}
              activeOpacity={0.8}
            >
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M8 12.6001V3" stroke="#151515" strokeWidth="1.5" strokeLinecap="square" />
                <Path d="M4 8.96669L8 13L12 8.96669" stroke="#151515" strokeWidth="1.5" strokeLinecap="square" />
              </Svg>
            </TouchableOpacity>
          </Animated.View>
        )}
          </View>
          </TouchableWithoutFeedback>

          {/* Chat Input - Inside flex flow, will be pushed up by KeyboardAvoidingView */}
          <View style={styles.inputSection}>
            {renderInputContent()}
          </View>

          {/* Bottom Navigation - Slides out smoothly in sync with keyboard */}
          <RNAnimated.View style={{ height: navbarContainerHeight, overflow: 'hidden' }}>
            <RNAnimated.View
              onLayout={onNavbarLayout}
              style={{ transform: [{ translateY: navbarTranslateY }] }}
            >
              <BillBottomNav activeTab="chat" />
            </RNAnimated.View>
          </RNAnimated.View>
        </View>
      </KeyboardAvoidingView>

      {/* Clear Conversation Confirmation Modal */}
      <BaseModal
        visible={showClearConversationModal}
        onClose={() => setShowClearConversationModal(false)}
        title={t('chat.clearConversation.title')}
        message={t('chat.clearConversation.message')}
        primaryButtonText={isClearing ? t('common.loading') : t('chat.clearConversation.confirm')}
        onPrimaryPress={handleClearConversationConfirm}
        primaryButtonDisabled={isClearing}
        secondaryButtonText={t('common.cancel')}
        onSecondaryPress={() => setShowClearConversationModal(false)}
        secondaryButtonDisabled={isClearing}
      />

      {/* Message long-press context menu — rendered in a top-level Modal so it
          always paints above all chat bubbles regardless of FlatList stacking. */}
      <MessageContextMenuOverlay
        visible={contextMenu.visible}
        anchor={contextMenu.anchor}
        isBookmarked={
          contextMenu.messageId
            ? bookmarkedMessages.has(contextMenu.messageId) ||
              (!contextMenu.isUser && isBillMessageSaved(contextMenu.messageId))
            : false
        }
        onShare={() =>
          contextMenu.messageId &&
          handleShareMessage(contextMenu.messageId, contextMenu.text, contextMenu.isUser)
        }
        onBookmark={() =>
          contextMenu.messageId &&
          handleBookmarkMessage(contextMenu.messageId, contextMenu.text)
        }
        onClose={handleCloseContextMenu}
      />
    </SafeAreaView>
  );

  function renderInputContent() {
    return (
      <>
          {/* Onboarding Carousel - replaces suggestions, hide when keyboard visible */}
          {showSuggestions && !isKeyboardVisibleDebounced && (
            <OnboardingCarousel
              onNavigateToSobriety={() => router.push('/sobriety-clock')}
              onNavigateToMeetings={() => router.push('/encontros-online')}
              onNavigateToTrail={() => router.push('/trail')}
              onNavigateToDiary={() => router.push({ pathname: '/diary', params: { openAddNote: 'true' } })}
              onNavigateToProfile={() => router.push('/perfil')}
              onSendFirstMessage={() => {
                // Focus on input and show a prompt
                setInputText(t('onboarding.firstMessagePrompt') || '');
              }}
            />
          )}

          {/* Bill Plus Bar - Show for ALL Base users (not Plus) */}
          {!hasPadrinhoPlus && (
            <LinearGradient 
              colors={['rgba(249, 248, 243, 0)', 'rgba(249, 248, 243, 1)']}
              locations={[0.8, 1]}
              style={styles.inputGradientContainer}
              pointerEvents="box-none"
            >
              <BillPlusBar 
                onUpgradePress={handleUpgradeToPlus}
                inputValue={inputText}
                onChangeText={handleTextChange}
                onSendPress={() => sendMessage(inputText.trim())}
                onResetPress={handleChatReset}
                inputPlaceholder={t('bill.input.placeholder')}
                sendDisabled={!inputText.trim() || dailyLimitReached}
                remainingMessages={remainingMessages}
              />
            </LinearGradient>
          )}

          {/* Regular Chat Input - Only show for Plus users */}
          {hasPadrinhoPlus && (
            <LinearGradient 
              colors={['rgba(249, 248, 243, 0)', 'rgba(249, 248, 243, 1)']}
              locations={[0.8, 1]}
              style={styles.inputGradientContainer}
              pointerEvents="box-none"
            >
              <View style={styles.textAreaContainer}>
                <View style={styles.textInputFrame}>
                  <TextInput
                    style={styles.textInput}
                    color="#151515"
                    value={inputText}
                    onChangeText={handleTextChange}
                    placeholder={t('bill.input.placeholder')}
                    placeholderTextColor="#A19F97"
                    multiline
                    maxLength={3000}
                    scrollEnabled
                  />
                </View>
                <View style={styles.buttonRow}>
                  <TouchableOpacity
                    style={styles.imageButton}
                    onPress={() => {
                      import('@/utils/clarityEvents')
                        .then(({ logUiAction }) =>
                          logUiAction({ surface: 'home', element: 'chat_reset' })
                        )
                        .catch(() => {});
                      handleChatReset();
                    }}
                  >
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M11.2817 10.683C10.4309 11.916 9.00395 12.7238 7.38848 12.7238C4.78225 12.7238 2.66602 10.613 2.66602 8.0013C2.66602 5.39504 4.78225 3.27881 7.38848 3.27881C9.95708 3.27881 12.0463 5.32504 12.1163 7.87743" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M13.334 5.39844L12.2619 7.93413L9.74023 6.86793" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    onPress={() => sendMessage(inputText.trim())}
                    style={styles.sendButton}
                    disabled={!inputText.trim()}
                  >
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M8.16602 3.3999V13" stroke="white" strokeWidth="1.5" strokeLinecap="square" />
                      <Path d="M12.183 7.03331L8.16704 3L4.15039 7.03331" stroke="white" strokeWidth="1.5" strokeLinecap="square" />
                    </Svg>
                  </TouchableOpacity>
                </View>
              </View>
            </LinearGradient>
          )}
      </>
    );
  }
}

export default BillScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
    position: 'relative',
  },
  contentWrapper: {
    flex: 1,
  },
  mainContent: {
    flex: 1,
    zIndex: 1,
  },
  fullScreenWrapper: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  avatarButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationButton: {
    padding: 4,
  },
  notificationIconContainer: {
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#E53E3E',
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  notificationBadgeText: {
    fontSize: 10,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  // Chat styles
  chatHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 24,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 15,
    backgroundColor: '#F9F8F3',
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  chatHeaderTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  chatContainer: {
    flex: 1,
  },
  chatContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 20,
  },
  welcomeLayout: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 40,
  },
  messageContainer: {
    maxWidth: '85%',
  },

  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#151515',
    borderRadius: 16,
    borderBottomRightRadius: 4,
    padding: 16,
  },
  agentMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 16,
  },
  messageText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',lineHeight: 22,
  },
  userMessageText: {
    color: '#FFFFFF',
  },
  agentMessageText: {
    color: '#151515',
  },

  billTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    marginTop: 32,
    textAlign: 'center',
  },
  billSubtitle: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    marginTop: 8,
    textAlign: 'center',
  },
  welcomeMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    maxWidth: '85%',
  },
  welcomeMessageText: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 22,
  },
  inputSection: {
    backgroundColor: '#F9F8F3',
  },
  inputGradientContainer: {
    paddingHorizontal: 12, 
    paddingBottom: 8, 
    paddingTop: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 14,
    elevation: 4,
  },
  chatContainerFullHeight: {
    flex: 1,
  },
  scrollViewFullHeight: {
    flex: 1,
  },
  messageAreaWrapper: {
    flex: 1,
    minHeight: '100%',
  },
  chatContentWithPadding: {
    paddingHorizontal: 16, // Align with input container
  },
  suggestionsWrapper: {
    position: 'absolute',
    bottom: 120, // Positioned directly above the chat input wrapper
    left: 0,
    right: 0,
    backgroundColor: '#F9F8F3',
    paddingBottom: 8,
    zIndex: 45, // Below the input wrapper's zIndex of 50
  },
  suggestionsWrapperInside: {
    backgroundColor: '#F9F8F3',
    paddingBottom: 8,
    marginBottom: 0,
  },
  suggestionsScrollView: {
    paddingHorizontal: 16,
    maxHeight: 50,
  },
  suggestionsContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    paddingRight: 32, // Add padding to ensure last chip is fully visible
  },
  suggestionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0EEE4',
    borderRadius: 17,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    gap: 8,
    alignSelf: 'flex-start',
  },
  suggestionText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 16.8,
  },
  textAreaContainer: {
    paddingTop: 16,
    paddingLeft: 12,
    paddingRight: 12,
    paddingBottom: 16,
    alignItems: 'flex-end',
    gap: 16,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    width: '100%',
    flexDirection: 'column',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  textInputFrame: {
    paddingLeft: 4,
    paddingRight: 4,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    flexDirection: 'row',
  },
  textInput: {
    color: '#151515',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 19.6, // 140% of 14px
    padding: 0,
    flex: 1,
    minHeight: 20,
    maxHeight: 160,
  },
  buttonRow: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    flexDirection: 'row',
  },
  imageButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    aspectRatio: 1,
    backgroundColor: '#F0EEE4',
    borderRadius: 27,
    width: 32,
    height: 32,
  },
  sendButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    aspectRatio: 1,
    backgroundColor: '#151515',
    borderRadius: 27,
    width: 32,
    height: 32,
  },
  sendButtonActive: {
    backgroundColor: '#151515',
  },
  sendButtonInactive: {
    backgroundColor: '#151515',
    opacity: 0.5,
  },
  scrollToBottomContainer: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    zIndex: 100,
  },
  scrollToBottomButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    aspectRatio: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 27,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    width: 32,
    height: 32,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.05,
    shadowRadius: 14,
    elevation: 4,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontStyle: 'italic',
    opacity: 0.7,
  },

  loadingDotsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  loadingDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#A19F97',
  },
  dateBadgeContainer: {
    position: 'absolute',
    top: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
  },
  scrollView: {
    flex: 1,
  },
  timestampText: {
    fontSize: 10,
    fontFamily: 'Inter_400Regular',
  },
  userTimestampText: {
    color: 'rgba(255, 255, 255, 0.6)',
  },
  agentTimestampText: {
    color: '#A19F97',
  },
  // Link styles
  linkText: {
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
  userLinkText: {
    color: '#87CEEB', // Light blue for user message links
  },
  agentLinkText: {
    color: '#015480', // Brand blue for agent message links
  },
  messageFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    marginTop: 4,
    gap: 6,
  },
  bookmarkIcon: {
    marginTop: 1,
  },
  bookmarkedMessage: {
    borderWidth: 1,
    borderColor: '#DAD8CF',
  },
  sendButtonContainer: {
    justifyContent: 'flex-end',
  },
  selectedMessage: {
    zIndex: 1000,
    elevation: 1000,
    shadowColor: '#000',
    shadowOffset: {
      width: 4,
      height: 4,
    },
    shadowOpacity: 0.06,
    shadowRadius: 4,
  },
  messageWrapper: {
    position: 'relative',
  },
  messageContainerWrapper: {
    width: '100%',
  },
  contextMenuOverlay: {
    position: 'absolute',
    bottom: '100%', // Position above the message card (bottom of menu at top of card)
    marginBottom: 8, // Small gap between menu and message
    zIndex: 10000,
    elevation: 10000,
  },
  contextMenuOverlayAgent: {
    left: 0, // Align to the left side of Bill's messages
  },
  contextMenuOverlayUser: {
    right: 0, // Align to the right for user messages (since user messages are right-aligned)
  },
  meetingsCarouselContainer: {
    width: '100%',
    marginTop: 12,
    marginBottom: 8,
  },
});